import {
  createContext,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";

import { listTimeZones, localTimeZone } from "./timezones";

export const TIME_ZONE_STORAGE_KEY = "ito.timeZone";

export interface TimeContextValue {
  /** The active IANA time zone id (e.g. `America/New_York`). */
  timeZone: string;
  /** Set the active zone. Invalid ids are ignored. */
  setTimeZone: (zone: string) => void;
  /** Reset to the browser's local zone. */
  useLocalTimeZone: () => void;
}

/**
 * Default context (no provider): local time. Component packages such as
 * `@itoresearch/terminal-renderer` can call `useTimezone()` without forcing
 * every host/test to mount a provider; they simply follow local time.
 */
export const FALLBACK_TIME_CONTEXT: TimeContextValue = {
  timeZone: localTimeZone(),
  setTimeZone: () => undefined,
  useLocalTimeZone: () => undefined,
};

export const TimeContext = createContext<TimeContextValue | null>(null);

export interface TimeProviderProps {
  children: ReactNode;
  /** Seed zone when nothing is persisted (defaults to the browser's local zone). */
  defaultTimeZone?: string;
}

/**
 * Wraps the app and controls the time zone every timestamp is rendered in.
 *
 * The selected zone is persisted to `localStorage` and read by `useTimezone` /
 * `useTimeFormatter`; defaulting to the browser's local zone when the user has
 * not chosen one. It is a product-wide concern analogous to `ThemeProvider`.
 */
export function TimeProvider({
  children,
  defaultTimeZone,
}: TimeProviderProps) {
  const [timeZone, setTimeZoneState] = useState<string>(() => {
    if (typeof window === "undefined") {
      return defaultTimeZone ?? localTimeZone();
    }
    const stored = window.localStorage.getItem(TIME_ZONE_STORAGE_KEY);
    if (stored && listTimeZones().includes(stored)) return stored;
    return defaultTimeZone ?? localTimeZone();
  });

  // Persist on change; guard storage access for sandboxed contexts.
  useLayoutEffect(() => {
    try {
      window.localStorage.setItem(TIME_ZONE_STORAGE_KEY, timeZone);
    } catch {
      /* storage unavailable — ignore */
    }
  }, [timeZone]);

  const setTimeZone = useCallback((zone: string) => {
    if (!zone) return;
    // Reject a zone the runtime cannot format with, so a bad value can never
    // crash a `DateTimeFormat` call downstream.
    try {
      new Intl.DateTimeFormat(undefined, { timeZone: zone });
    } catch {
      return;
    }
    setTimeZoneState(zone);
  }, []);

  const useLocalTimeZone = useCallback(
    () => setTimeZoneState(localTimeZone()),
    [],
  );

  const value = useMemo<TimeContextValue>(
    () => ({ timeZone, setTimeZone, useLocalTimeZone }),
    [timeZone, setTimeZone, useLocalTimeZone],
  );

  return <TimeContext.Provider value={value}>{children}</TimeContext.Provider>;
}
